import 'C:\\Projects\\genkei\\zumeco\\static\\folder\\DoodleList.tsx.esbuild-plugin-linaria.css';
import 'C:\\Projects\\genkei\\zumeco\\static\\folder\\DoodleList.tsx.esbuild-plugin-windicss.css';
import { css, cx } from '@linaria/core';
import { mdiCheck, mdiClock, mdiDeleteOutline, mdiFileDownloadOutline, mdiFolder, mdiFolderHome, mdiShare } from '@mdi/js';
import { JSX } from 'preact';
import { memo } from 'preact/compat';
import { useCallback, useMemo } from 'preact/hooks';
import '../../muicss/mui.css';
import { doodleStorageSize } from '../../server/src/doodleStorageSize';
import type { CurrentFolderUser, Doodle, DoodleUser } from '../../server/src/models';
import { URL_DOODLE, URL_FOLDER, URL_IMAGE_THUMBNAIL } from '../../server/src/urls';
import { COLOR_ACTIVE_BACK, COLOR_BACK, COLOR_SHADOW } from '../shared/colors';
import '../shared/common.css';
import { Icon, IconAndText } from '../shared/components/Icon';
import { ToolbarButton } from '../shared/components/ToolbarButton';
import { useTenantUsersWithoutCurrentUser } from '../shared/data/tenantUsers';
import { i18n } from '../shared/i18n';
import { THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../shared/uploadFile';
import { noop } from '../shared/utils';
import { openChangeDoodleOwnerDialog } from './ChangeOwnerDialog';
import { deleteDoodle } from './data/doodles';
import { getFolder } from './data/folders';
import { onDragDoodleEnd, onDragDoodleStart } from './dropLink';
import './folder.css';
import { ENABLE_HANDOVER, toggleSelectDoodle, useDoodleListFormat, useSelectedDoodles } from './folderState';
import { enqueuePdfDownloadDoodle, usePdfDownloadDoodleQueue, usePdfDownloadedDoodleIds } from './PdfDownloadStatusPanel';
const prettyBytes = (bytes: number) => bytes > 999_999 ? `${Math.ceil(bytes / (1024 * 102.4)) / 10} MB` : `${Math.ceil(bytes / 1024) || 0} KB`;
const userDisplayName = (user: DoodleUser | undefined) => user ? user.name || user.email || user.uid : '';
const getFolderName = (folderId: string) => folderId ? getFolder(folderId)?.name ?? '' : i18n.rootFolderName;
const formatDateTime = (() => {
  const now = new Date();
  return (time: number | undefined) => {
    if (time) {
      const date = new Date(time);
      return now.getFullYear() === date.getFullYear() ? i18n.MMddHHmm(date) : i18n.yyyyMMdd(date);
    }
  };
})();
const Timestamp = memo(({
  className,
  time
}: {
  className?: string;
  time: number | undefined;
}) => <time className={cx('break-normal font-size-13px', className)} dateTime={time ? new Date(time).toISOString() : undefined} title={time ? i18n.formatDateTime(time, {
  year: true
}) : undefined} children={formatDateTime(time)} />);
const SelectionNumber = ({
  selectedIndex
}: {
  selectedIndex: number;
}) => {
  const selectedNumber = selectedIndex + 1;
  return <span class={cx('select-none rounded-1/2 w-1.5em h-1.5em flex items-center justify-center text-white border-primary', selectedNumber ? 'bg-primary' : 'bg-white')} children={selectedNumber || undefined} />;
};
const CardButton = memo(({
  icon,
  text,
  priority,
  disabled,
  onClick
}: {
  icon: string;
  text: string;
  priority?: 'danger';
  disabled?: boolean;
  onClick: Parameters<typeof ToolbarButton>[0]['onClick'];
}) => <ToolbarButton className="w-2.25em" priority={priority} disabled={disabled} onClick={onClick}>
      <IconAndText icon={icon} text={text} />
    </ToolbarButton>);
const DoodleDownloadButton = memo(({
  doodle,
  disabled
}: {
  doodle: Doodle;
  disabled?: boolean;
}) => {
  const pdfDownloadDoodleQueue = usePdfDownloadDoodleQueue();
  const isPdfDownloadQueued = useMemo(() => pdfDownloadDoodleQueue.some(({
    id
  }) => id === doodle.id), [doodle.id, pdfDownloadDoodleQueue]);
  const pdfDownloadedDoodleIds = usePdfDownloadedDoodleIds();
  const isPdfDownloaded = useMemo(() => pdfDownloadedDoodleIds.has(doodle.id), [pdfDownloadedDoodleIds, doodle.id]);
  const onClickDownloadPdf = useCallback(() => enqueuePdfDownloadDoodle(doodle), [doodle]);
  return isPdfDownloadQueued ? <CardButton disabled text="PDF" icon={mdiClock} onClick={noop} /> : <CardButton disabled={disabled} text="PDF" icon={isPdfDownloaded ? mdiCheck : mdiFileDownloadOutline} onClick={onClickDownloadPdf} />;
});
const DoodleCardButtons = memo(({
  doodle,
  currentUser,
  disabled
}: {
  doodle: Doodle;
  currentUser: CurrentFolderUser;
  disabled?: boolean;
}) => {
  const tenantUsers = useTenantUsersWithoutCurrentUser();
  const onClickHandover = useCallback(() => openChangeDoodleOwnerDialog(doodle), [doodle]);
  const onClickDelete = useCallback(() => confirm(i18n.confirmDelete) && deleteDoodle(doodle), [doodle]);
  return <div class="px-3 flex opacity-80" style="border-top: 1px solid #ccc">
        <DoodleDownloadButton doodle={doodle} disabled={disabled} />
        {doodle.ownerUserId === currentUser.uid && <>
            <div class="flex-auto" />
            {ENABLE_HANDOVER && <CardButton icon={mdiShare} text={i18n.handover.handover} disabled={disabled || !tenantUsers} onClick={onClickHandover} />}
            <CardButton icon={mdiDeleteOutline} text={i18n.delete} disabled={disabled} priority="danger" onClick={onClickDelete} />
          </>}
      </div>;
});
const DoodleCardProperty = ({
  label,
  children
}: {
  label: string;
  children: JSX.Element | string;
}) => <div>
    <div class="pb-3px text-primary font-size-11px font-bold opacity-90">{label}</div>
    <div>{children}</div>
  </div>;
const DoodleCardProperties = ({
  doodle
}: {
  doodle: Doodle;
}) => <div class="grid grid-cols-2 gap-2 py-1 font-size-14px">
    <DoodleCardProperty label={i18n.ownedBy} children={userDisplayName(doodle.users[doodle.ownerUserId])} />
    <DoodleCardProperty label={i18n.storageUsage} children={<pre class="font-size-13px">{prettyBytes(doodleStorageSize(doodle))}</pre>} />
    <DoodleCardProperty label={i18n.createdBy} children={userDisplayName(doodle.users[doodle.authorUserId])} />
    <DoodleCardProperty label={i18n.createdAt} children={<Timestamp time={doodle.createdAt} />} />
    <DoodleCardProperty label={i18n.updatedBy} children={userDisplayName(doodle.users[doodle.updatedBy!])} />
    <DoodleCardProperty label={i18n.updatedAt} children={<Timestamp time={doodle.updatedAt} />} />
  </div>;
const DoodleCardContent = ({
  doodle,
  tenantSharing
}: {
  doodle: Doodle;
  tenantSharing?: boolean;
}) => <div class="text-15px leading-tight hover:text-hex-$primary-color">
    <div class="box-content pt-3 px-3 flex items-center justify-center overflow-hidden relative" style={{
    background: COLOR_BACK,
    borderBottom: `1px solid ${COLOR_BACK}`,
    height: THUMBNAIL_HEIGHT
  }}>
      <img alt="thumbnail" loading="lazy" src={URL_IMAGE_THUMBNAIL(doodle.id)} style={{
      maxWidth: THUMBNAIL_WIDTH,
      maxHeight: THUMBNAIL_HEIGHT,
      boxShadow: `0 0 8px ${COLOR_SHADOW}`
    }} />
      {!!(doodle.finalizedAt || doodle.blackedOut) && <div class="absolute top-2 left-2 flex items-center justify-center py-1 px-2 rounded-full leading-none" style={{
      background: 'var(--primary-color)',
      color: COLOR_ACTIVE_BACK
    }}>
          {doodle.finalizedAt ? i18n.finalized : i18n.blackOut.blackedOut}
        </div>}
      <div class="absolute top-2 right-2 px-2 py-1 rounded-full text-sm leading-none bg-white text-hex-$primary-color border border-current">
        {tenantSharing ? `${i18n.sharing.sharedGuestCount} ${i18n.sharing.nPeople(doodle.uids.map(uid => doodle.users[uid]).filter(user => user.uid !== doodle.ownerUserId && !user.tenantShared).length)}` : `${i18n.sharing.sharedUserCount} ${i18n.sharing.nPeople(doodle.uids.length - 1)}`}
      </div>
    </div>
    <div class="hover:text-hex-$primary-color py-2 px-3">
      <h3 class="text-base leading-tight overflow-hidden h-10 mb-1 line-clamp-2" title={doodle.filename} children={doodle.filename} />
      <DoodleCardProperties doodle={doodle} />
    </div>
  </div>;
const DoodleCard = memo(({
  className,
  doodle,
  currentUser,
  tenantSharing,
  disabled
}: {
  className?: string;
  doodle: Doodle;
  currentUser: CurrentFolderUser;
  tenantSharing?: boolean;
  disabled?: boolean;
}) => <div class={cx('relative w-full min-h-256px break-all overflow-hidden flex flex-col', "dps8zu", className)}>
      <a data-doodle-id={doodle.id} href={disabled ? undefined : URL_DOODLE(doodle.id)} class={cx('user-drag-auto flex-auto block', doodle.finalizedAt && 'doodle-finalized')} draggable onDragStart={onDragDoodleStart} onDragEnd={onDragDoodleEnd}>
        <DoodleCardContent doodle={doodle} tenantSharing={tenantSharing} />
      </a>
      {!!currentUser.permissions?.output && <DoodleCardButtons doodle={doodle} currentUser={currentUser} disabled={disabled} />}
    </div>);
const DoodleCards = memo(({
  currentUser,
  doodles,
  tenantSharing,
  showsFolderName
}: {
  currentUser: CurrentFolderUser;
  doodles: readonly Doodle[] | undefined;
  tenantSharing?: boolean;
  showsFolderName?: boolean;
}) => {
  const selectedDoodles = useSelectedDoodles();
  const selecting = !!selectedDoodles;
  return <ul class="list-none p-0 grid gap-4 xl:gap-6" style="grid-template-columns: repeat(auto-fill, minmax(240px, 1fr))">
        {doodles?.map(doodle => {
      const selectedIndex = selectedDoodles?.indexOf(doodle) ?? -1;
      const selected = selectedIndex !== -1;
      const folder = showsFolderName ? getFolder(doodle.parentFolderId) : undefined;
      return <li key={doodle.id} class="flex flex-col">
              {showsFolderName && <a class="flex items-center gap-1 w-max mb-0.5 hover:text-hex-$primary-color hover:underline" href={URL_FOLDER(doodle.parentFolderId)}>
                  <Icon icon={folder?.tenantSharing ? mdiFolderHome : mdiFolder} />
                  {doodle.parentFolderId ? folder?.name ?? '' : i18n.rootFolderName}
                </a>}
              <div class="flex-1 relative bg-white">
                <div class={cx('h-full flex items-stretch justify-stretch transform transition-transform', selected && 'scale-90')}>
                  <DoodleCard doodle={doodle} currentUser={currentUser} tenantSharing={tenantSharing} disabled={selecting} />
                </div>
                {selecting && <div class={cx('absolute inset-0 p-1 cursor-pointer', selected && 'outline-primary')} onClick={() => toggleSelectDoodle(doodle)}>
                    <SelectionNumber selectedIndex={selectedIndex} />
                  </div>}
              </div>
            </li>;
    })}
      </ul>;
});
const cssDoodleTable = "cr35day";
const DoodleTableRow = memo(({
  doodle,
  selecting,
  selectedIndex,
  showsFolderName
}: {
  doodle: Doodle;
  selecting: boolean;
  selectedIndex: number;
  showsFolderName?: boolean;
}) => {
  const cell = (content: string | JSX.Element, className?: string) => <td class={className}>
        <span class="doodle-table-cell-content">{content}</span>
      </td>;
  const nameCell = (uid: string) => cell(userDisplayName(doodle.users[uid]), 'w-8em max-w-max');
  const timeCell = (time: number) => cell(<Timestamp className="w-8.5em text-right" time={time} />);
  return <tr class={selecting ? 'cursor-pointer' : undefined} onClick={selecting ? () => toggleSelectDoodle(doodle) : undefined}>
        {selecting && <td class="!py-0 !pr-0 !pl-2">
            <SelectionNumber selectedIndex={selectedIndex} />
          </td>}
        {<td>
            {selecting ? <span class="doodle-table-cell-content">{doodle.filename}</span> : <a data-doodle-id={doodle.id} class="user-drag-auto doodle-table-cell-content hover:underline" href={URL_DOODLE(doodle.id)} draggable onDragStart={onDragDoodleStart} onDragEnd={onDragDoodleEnd} children={doodle.filename} />}
          </td>}
        {showsFolderName && <td>
            {selecting ? <span class="doodle-table-cell-content">{getFolderName(doodle.parentFolderId)}</span> : <a class="doodle-table-cell-content hover:underline" href={URL_FOLDER(doodle.parentFolderId)}>
                {getFolderName(doodle.parentFolderId)}
              </a>}
          </td>}
        {nameCell(doodle.ownerUserId)}
        {cell(<pre class="text-right">{prettyBytes(doodleStorageSize(doodle))}</pre>, 'w-1px')}
        {nameCell(doodle.authorUserId)}
        {timeCell(doodle.createdAt)}
        {nameCell(doodle.updatedBy!)}
        {timeCell(doodle.updatedAt)}
      </tr>;
});
const DoodleTable = memo(({
  doodles,
  showsFolderName
}: {
  doodles: readonly Doodle[] | undefined;
  showsFolderName?: boolean;
}) => {
  const selectedDoodles = useSelectedDoodles();
  if (!doodles) {
    return null;
  }
  const selecting = !!selectedDoodles;
  return <table class={`w-full whitespace-pre-wrap break-all leading-tight font-size-14px xl:font-size-15px ${cssDoodleTable}`}>
      <thead class="whitespace-nowrap">
        <tr>
          {selecting && <th class="!p-0" />}
          <th>{i18n.filename}</th>
          {showsFolderName && <th>{i18n.folderName}</th>}
          <th>{i18n.ownedBy}</th>
          <th>{i18n.storage}</th>
          <th>{i18n.createdBy}</th>
          <th>{i18n.createdAt}</th>
          <th>{i18n.updatedBy}</th>
          <th>{i18n.updatedAt}</th>
        </tr>
      </thead>
      <tbody>
        {doodles.map(doodle => <DoodleTableRow key={doodle.id} doodle={doodle} selecting={selecting} selectedIndex={selectedDoodles?.indexOf(doodle) ?? -1} showsFolderName={showsFolderName} />)}
      </tbody>
    </table>;
});
export const DoodleList = memo((props: {
  currentUser: CurrentFolderUser;
  doodles: readonly Doodle[] | undefined;
  tenantSharing?: boolean;
  showsFolderName?: boolean;
}) => {
  switch (useDoodleListFormat()) {
    case 'card':
      return <DoodleCards {...props} />;
    case 'table':
      return <DoodleTable doodles={props.doodles} showsFolderName={props.showsFolderName} />;
  }
});